/**
 * PromptAligner
 *
 * Provides geometric alignment operations for line prompts:
 * - Parallel alignment (snap directions to shared angle)
 * - Collinear alignment (project positions onto shared line)
 * - Corner alignment (make two groups exactly 90°)
 * - Screen-space box selection
 * - Infinite line intersection and collinearity checks
 */

import * as THREE from 'three';
import { LinePrompt, PromptConnection } from './types';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

/**
 * Local-frame view of a prompt: same `worldPoint`/`direction` Vector3
 * references the public API uses, but their values have been transformed
 * into the active base-plane local frame (u, v, normal). Statics that
 * compute XY perpendiculars, atan2 angles, and dot products read
 * directly off these vectors and produce results consistent with the
 * pre-frame world-XY behavior — for rooms with the identity-XY frame
 * the local values equal the world values.
 *
 * Constructed at instance-method entry via `toLocalView`; the inverse
 * `flushLocalViewToWorld` writes the (possibly-mutated) local values
 * back to the prompt's world-space `worldPoint`/`direction` fields.
 */
interface PromptLocalView {
  prompt: LinePrompt;
  worldPoint: THREE.Vector3; // local-frame
  direction: THREE.Vector3;  // local-frame
}

function toLocalView(prompt: LinePrompt, t: CoordinateTransformer): PromptLocalView {
  return {
    prompt,
    worldPoint: t.toBasePlaneLocal(prompt.worldPoint),
    direction: t.toBasePlaneLocalDir(prompt.direction),
  };
}

function flushLocalViewToWorld(view: PromptLocalView, t: CoordinateTransformer): void {
  view.prompt.worldPoint.copy(t.fromBasePlaneLocal(view.worldPoint));
  view.prompt.direction.copy(t.fromBasePlaneLocalDir(view.direction));
}

export class PromptAligner {
  /** |cos| > 0.95 ≈ < 18° — threshold for treating directions as parallel */
  static readonly ALIGN_PARALLEL_COS = 0.95;
  /** |cos| < 0.26 ≈ 75–105° band — threshold for corner detection */
  static readonly ALIGN_CORNER_COS = 0.26;
  /** Max point-to-line distance as fraction of selection extent */
  static readonly ALIGN_COLLINEAR_RATIO = 0.02;

  constructor(private coordinateTransformer: CoordinateTransformer) {}

  /**
   * Compute the average angle (in radians) of a set of directions.
   * Handles anti-parallel directions by doubling angles before averaging
   * (treats direction as an undirected axis).
   */
  static averageAngle(directions: THREE.Vector3[]): number {
    let sx = 0,
      sy = 0;
    for (const d of directions) {
      const theta = Math.atan2(d.y, d.x);
      sx += Math.cos(2 * theta);
      sy += Math.sin(2 * theta);
    }
    return Math.atan2(sy, sx) / 2;
  }

  /** Build a unit THREE.Vector3 in XY from an angle */
  static dir3DFromAngle(angle: number): THREE.Vector3 {
    return new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
  }

  /**
   * Group prompts by parallel direction.
   * Returns groups where every member is within ALIGN_PARALLEL_COS of the group seed.
   */
  groupByParallelDirection(prompts: LinePrompt[]): { angle: number; prompts: LinePrompt[] }[] {
    const groups: { angle: number; prompts: LinePrompt[] }[] = [];

    for (const prompt of prompts) {
      const theta = Math.atan2(prompt.direction.y, prompt.direction.x);
      let found = false;
      for (const group of groups) {
        const diff = theta - group.angle;
        const absCos = Math.abs(Math.cos(diff));
        if (absCos > PromptAligner.ALIGN_PARALLEL_COS) {
          group.prompts.push(prompt);
          found = true;
          break;
        }
      }
      if (!found) {
        groups.push({ angle: theta, prompts: [prompt] });
      }
    }
    return groups;
  }

  /**
   * Check whether a group of parallel prompts is collinear.
   * For each pair, compute the perpendicular distance from one prompt's
   * worldPoint to the other prompt's infinite line (worldPoint + direction).
   * If ALL pairwise distances are below the threshold, they are collinear.
   */
  static isGroupCollinear(prompts: LinePrompt[], avgDir: THREE.Vector3, threshold: number): boolean {
    const perpDir = new THREE.Vector3(-avgDir.y, avgDir.x, 0);
    for (let i = 0; i < prompts.length; i++) {
      for (let j = i + 1; j < prompts.length; j++) {
        const toI = new THREE.Vector3().subVectors(prompts[i].worldPoint, prompts[j].worldPoint);
        const dist = Math.abs(toI.dot(perpDir));
        if (dist >= threshold) return false;
      }
    }
    return true;
  }

  /**
   * Compute the spatial extent (max distance between any two prompts) of a set of prompts.
   */
  static selectionExtent(prompts: LinePrompt[]): number {
    let maxDist = 0;
    for (let i = 0; i < prompts.length; i++) {
      for (let j = i + 1; j < prompts.length; j++) {
        const d = prompts[i].worldPoint.distanceTo(prompts[j].worldPoint);
        if (d > maxDist) maxDist = d;
      }
    }
    return maxDist;
  }

  /**
   * Classify and align prompts inside a screen-space box.
   *
   * Parallel: rotate all prompts to their average combined angle.
   * Collinear: same rotation + project positions onto a single shared line.
   * Corner: two groups ~90° apart → rotate both minimally so they are exactly 90°.
   */
  alignPromptsInBox(prompts: LinePrompt[], start: THREE.Vector2, end: THREE.Vector2): void {
    const selectedPrompts = this.getPromptsInScreenBox(prompts, start, end);
    if (selectedPrompts.length < 2) return;

    const t = this.coordinateTransformer;

    // Build local views once. The static helpers (averageAngle,
    // dir3DFromAngle, isGroupCollinear, selectionExtent) read directly
    // from worldPoint/direction Vector3s; we hand them the view's
    // local-frame vectors so all math runs in (u, v).
    const viewById = new Map<LinePrompt, PromptLocalView>();
    for (const p of selectedPrompts) viewById.set(p, toLocalView(p, t));

    // Helper that returns local-view "stand-in prompts" with the SAME
    // shape as LinePrompt for static methods that read .direction / .worldPoint.
    const viewAsPromptLike = (p: LinePrompt): LinePrompt => {
      const v = viewById.get(p);
      if (!v) throw new Error('PromptAligner: missing local view for prompt');
      return { ...p, worldPoint: v.worldPoint, direction: v.direction };
    };

    const localSelected = selectedPrompts.map(viewAsPromptLike);

    const extent = PromptAligner.selectionExtent(localSelected);
    const collinearThreshold = extent * PromptAligner.ALIGN_COLLINEAR_RATIO;

    const allGroupsLocal = this.groupByParallelDirection(localSelected);

    // ── Corner detection: pairs of groups that are roughly 90° ──
    for (let i = 0; i < allGroupsLocal.length; i++) {
      for (let j = i + 1; j < allGroupsLocal.length; j++) {
        const gA = allGroupsLocal[i];
        const gB = allGroupsLocal[j];
        const angleA = PromptAligner.averageAngle(gA.prompts.map((p) => p.direction));
        const angleB = PromptAligner.averageAngle(gB.prompts.map((p) => p.direction));

        const dirA = PromptAligner.dir3DFromAngle(angleA);
        const dirB = PromptAligner.dir3DFromAngle(angleB);
        const absDot = Math.abs(dirA.dot(dirB));
        if (absDot >= PromptAligner.ALIGN_CORNER_COS) continue;

        const perpAngle = angleA + Math.PI / 2;
        const perpDir = PromptAligner.dir3DFromAngle(perpAngle);
        const targetAngleB = perpDir.dot(dirB) >= 0 ? perpAngle : perpAngle + Math.PI;

        let errorB = targetAngleB - angleB;
        errorB = Math.atan2(Math.sin(errorB), Math.cos(errorB));

        const halfCorr = errorB / 2;
        const newAngleA = angleA - halfCorr;
        const newAngleB = angleB + halfCorr;

        const newDirA = PromptAligner.dir3DFromAngle(newAngleA);
        const newDirB = PromptAligner.dir3DFromAngle(newAngleB);

        for (const p of gA.prompts) {
          // p.direction is a reference into the view's local direction.
          if (p.direction.dot(newDirA) < 0) p.direction.copy(newDirA.clone().negate());
          else p.direction.copy(newDirA);
        }
        for (const p of gB.prompts) {
          if (p.direction.dot(newDirB) < 0) p.direction.copy(newDirB.clone().negate());
          else p.direction.copy(newDirB);
        }
      }
    }

    // ── Parallel / Collinear alignment within each group ──
    const alignableGroups = allGroupsLocal.filter((g) => g.prompts.length >= 2);

    for (const group of alignableGroups) {
      const groupPrompts = group.prompts;
      const avgAngle = PromptAligner.averageAngle(groupPrompts.map((p) => p.direction));
      const avgDir3D = PromptAligner.dir3DFromAngle(avgAngle);

      const isCollinear = PromptAligner.isGroupCollinear(groupPrompts, avgDir3D, collinearThreshold);

      const avgPos = new THREE.Vector3();
      if (isCollinear) {
        for (const p of groupPrompts) avgPos.add(p.worldPoint);
        avgPos.divideScalar(groupPrompts.length);
      }

      for (const p of groupPrompts) {
        if (p.direction.dot(avgDir3D) < 0) p.direction.copy(avgDir3D.clone().negate());
        else p.direction.copy(avgDir3D);

        if (isCollinear) {
          const toP = new THREE.Vector3().subVectors(p.worldPoint, avgPos);
          const along = toP.dot(avgDir3D);
          p.worldPoint.copy(avgPos.clone().add(avgDir3D.clone().multiplyScalar(along)));
        }
      }
    }

    // Flush local-view mutations back to world.
    for (const view of viewById.values()) {
      flushLocalViewToWorld(view, t);
      view.prompt.screenPosition = t.worldToScreen(view.prompt.worldPoint);
    }
  }

  /**
   * Classify prompts in a screen-space box and return a label describing what
   * alignment action would be performed.
   */
  classifyAlignAction(prompts: LinePrompt[], start: THREE.Vector2, end: THREE.Vector2): { label: string; actionable: boolean } {
    const selected = this.getPromptsInScreenBox(prompts, start, end);
    if (selected.length < 2) {
      return { label: selected.length === 1 ? 'Select 1+ more' : 'No prompts', actionable: false };
    }

    const t = this.coordinateTransformer;
    // Run the classification against local-frame views so angles and
    // perpendiculars match `alignPromptsInBox` exactly. classifyAlignAction
    // is read-only on the prompt — no flush back is needed.
    const localSelected = selected.map((p) => {
      const v = toLocalView(p, t);
      return { ...p, worldPoint: v.worldPoint, direction: v.direction } as LinePrompt;
    });

    const extent = PromptAligner.selectionExtent(localSelected);
    const collinearThreshold = extent * PromptAligner.ALIGN_COLLINEAR_RATIO;

    const allGroups = this.groupByParallelDirection(localSelected);

    let cornerCount = 0;
    for (let i = 0; i < allGroups.length; i++) {
      for (let j = i + 1; j < allGroups.length; j++) {
        const angleA = PromptAligner.averageAngle(allGroups[i].prompts.map((p) => p.direction));
        const angleB = PromptAligner.averageAngle(allGroups[j].prompts.map((p) => p.direction));
        const absCos = Math.abs(Math.cos(angleA - angleB));
        if (absCos < PromptAligner.ALIGN_CORNER_COS) {
          cornerCount++;
        }
      }
    }

    const alignable = allGroups.filter((g) => g.prompts.length >= 2);
    let parallelCount = 0;
    let collinearCount = 0;

    for (const group of alignable) {
      const avgAngle = PromptAligner.averageAngle(group.prompts.map((p) => p.direction));
      const avgDir = PromptAligner.dir3DFromAngle(avgAngle);

      if (PromptAligner.isGroupCollinear(group.prompts, avgDir, collinearThreshold)) {
        collinearCount += group.prompts.length;
      } else {
        parallelCount += group.prompts.length;
      }
    }

    if (cornerCount === 0 && parallelCount === 0 && collinearCount === 0) {
      return { label: `${selected.length} prompts — no alignable pairs`, actionable: false };
    }

    const parts: string[] = [];
    if (cornerCount > 0) parts.push(`Corner ${cornerCount}`);
    if (collinearCount > 0) parts.push(`Collinear ${collinearCount}`);
    if (parallelCount > 0) parts.push(`Parallel ${parallelCount}`);
    return { label: parts.join(' + '), actionable: true };
  }

  /** Get all prompts whose worldPoint falls inside a screen-space box */
  getPromptsInScreenBox(prompts: LinePrompt[], start: THREE.Vector2, end: THREE.Vector2): LinePrompt[] {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const result: LinePrompt[] = [];
    for (const prompt of prompts) {
      const screenPos = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      if (screenPos.x >= minX && screenPos.x <= maxX && screenPos.y >= minY && screenPos.y <= maxY) {
        result.push(prompt);
      }
    }
    return result;
  }

  /**
   * Intersect two infinite lines in the base-plane local (u, v). Inputs
   * are in world coords; the optional `transformer` round-trips through
   * the active `BasePlaneFrame` so vertical (door/window) planes
   * intersect correctly. Without a transformer (or with no installed
   * frame) this is the pre-frame world-XY intersection — identical
   * behavior for rooms and for non-LineTool callers.
   *
   * Returns null if the lines are parallel in the base plane.
   */
  static intersectInfiniteLines(p1: THREE.Vector3, d1: THREE.Vector3, p2: THREE.Vector3, d2: THREE.Vector3, transformer?: CoordinateTransformer): THREE.Vector3 | null {
    const tx = transformer && transformer.getBasePlaneFrame() ? transformer : null;
    const lp1 = tx ? tx.toBasePlaneLocal(p1) : p1;
    const ld1 = tx ? tx.toBasePlaneLocalDir(d1) : d1;
    const lp2 = tx ? tx.toBasePlaneLocal(p2) : p2;
    const ld2 = tx ? tx.toBasePlaneLocalDir(d2) : d2;

    const denom = ld1.x * ld2.y - ld1.y * ld2.x;
    if (Math.abs(denom) < 1e-10) return null;
    const t = ((lp2.x - lp1.x) * ld2.y - (lp2.y - lp1.y) * ld2.x) / denom;
    // Preserve lp1.z (pre-frame: caller's world Z; with frame: local Z,
    // typically 0 for plane-resident prompts). The round-trip restores
    // the original Z on the world side.
    const local = new THREE.Vector3(lp1.x + t * ld1.x, lp1.y + t * ld1.y, lp1.z);
    return tx ? tx.fromBasePlaneLocal(local) : local;
  }

  /**
   * Check if two prompts are collinear: nearly parallel directions and
   * small perpendicular distance between their worldPoints — measured
   * in the base-plane local frame so vertical (door/window) planes
   * resolve "collinearity along the wall" correctly. Without a frame
   * (or for non-LineTool callers that omit the transformer), falls
   * back to the pre-frame world-XY perp distance.
   */
  static areCollinear(a: LinePrompt, b: LinePrompt, cosThreshold: number, distThreshold: number, transformer?: CoordinateTransformer): boolean {
    const tx = transformer && transformer.getBasePlaneFrame() ? transformer : null;
    const aDir = tx ? tx.toBasePlaneLocalDir(a.direction) : a.direction;
    const bDir = tx ? tx.toBasePlaneLocalDir(b.direction) : b.direction;
    const absDot = Math.abs(aDir.dot(bDir));
    if (absDot < cosThreshold) return false;

    const aPt = tx ? tx.toBasePlaneLocal(a.worldPoint) : a.worldPoint;
    const bPt = tx ? tx.toBasePlaneLocal(b.worldPoint) : b.worldPoint;
    const toB = new THREE.Vector3().subVectors(bPt, aPt);
    const perpDist = Math.abs(toB.dot(new THREE.Vector3(-aDir.y, aDir.x, 0)));
    return perpDist < distThreshold;
  }

  /**
   * Merge two collinear prompts into one. Extends promptA to cover both
   * prompts' endpoints along the shared direction, then removes promptB
   * from the prompts array.
   */
  mergePromptPair(promptA: LinePrompt, promptB: LinePrompt, prompts: LinePrompt[], floorHeight: number | null): void {
    const t = this.coordinateTransformer;
    const hasFrame = t.getBasePlaneFrame() != null;

    // Build local-frame views — geometry happens in (u, v). For rooms
    // with the identity-XY frame this is byte-equivalent to the
    // pre-frame world-XY math.
    const viewA = toLocalView(promptA, t);
    const viewB = toLocalView(promptB, t);

    const dir = viewA.direction.clone().normalize();

    const aPos = viewA.worldPoint.clone().add(dir.clone().multiplyScalar(promptA.positiveLength ?? 0));
    const aNeg = viewA.worldPoint.clone().add(dir.clone().multiplyScalar(-(promptA.negativeLength ?? 0)));
    const bPos = viewB.worldPoint.clone().add(dir.clone().multiplyScalar(viewB.direction.dot(dir) >= 0 ? (promptB.positiveLength ?? 0) : -(promptB.positiveLength ?? 0)));
    const bNeg = viewB.worldPoint.clone().add(dir.clone().multiplyScalar(viewB.direction.dot(dir) >= 0 ? -(promptB.negativeLength ?? 0) : (promptB.negativeLength ?? 0)));

    const allPoints = [aPos, aNeg, bPos, bNeg];
    const projections = allPoints.map((p) => new THREE.Vector3().subVectors(p, viewA.worldPoint).dot(dir));

    const maxProj = Math.max(...projections);
    const minProj = Math.min(...projections);
    const midProj = (maxProj + minProj) / 2;

    const newLocalPoint = viewA.worldPoint.clone().add(dir.clone().multiplyScalar(midProj));
    // Snap to the base plane: with a frame installed, local z=0 puts the
    // prompt on-plane; without a frame, fall back to the legacy
    // `wp.z = floorHeight` for backwards compat with non-LineTool callers.
    if (hasFrame) {
      newLocalPoint.z = 0;
    } else if (floorHeight !== null) {
      newLocalPoint.z = floorHeight;
    }

    const newWorldPoint = hasFrame ? t.fromBasePlaneLocal(newLocalPoint) : newLocalPoint;

    promptA.worldPoint.copy(newWorldPoint);
    promptA.positiveLength = maxProj - midProj;
    promptA.negativeLength = midProj - minProj;
    promptA.screenPosition = t.worldToScreen(newWorldPoint);

    const bIdx = prompts.indexOf(promptB);
    if (bIdx >= 0) {
      prompts.splice(bIdx, 1);
    }
  }

  /**
   * Clean up short edges around a prompt and merge collinear neighbors.
   * Shared by drag cleanup and bridge insertion.
   *
   * 1. Find neighbors of targetPrompt that are short (<5% of target's wall length)
   * 2. Find second neighbors (the prompts beyond the short ones)
   * 3. Check collinearity — merge if possible
   * 4. Remove short edges
   *
   * Returns the updated prompts array (may have removed elements).
   */
  cleanupEdgesAroundPrompt(targetPrompt: LinePrompt, prompts: LinePrompt[], floorHeight: number | null, onShapeUpdate: (prompts: LinePrompt[]) => void): LinePrompt[] {
    onShapeUpdate(prompts);

    if (prompts.length < 3) return prompts;

    const posConn = targetPrompt.connections?.positive;
    const negConn = targetPrompt.connections?.negative;
    if (!posConn || !negConn) return prompts;

    // Threshold is 5% of the target edge's wall length
    let targetWallLength = 0;
    if (posConn.point && negConn.point) {
      targetWallLength = posConn.point.distanceTo(negConn.point);
    }
    const minEdgeLength = Math.min(targetWallLength * 0.05, 0.05);

    const neighbors: Array<{ neighbor: LinePrompt; isShort: boolean }> = [];

    for (const conn of [posConn, negConn]) {
      const idx = conn.targetPromptIndex;
      if (idx < 0 || idx >= prompts.length) continue;
      const neighbor = prompts[idx];

      const posLen = neighbor.positiveLength ?? Infinity;
      const negLen = neighbor.negativeLength ?? Infinity;
      const wallLength = posLen === Infinity || negLen === Infinity ? Infinity : posLen + negLen;

      neighbors.push({ neighbor, isShort: wallLength < minEdgeLength });
    }

    const hasShort = neighbors.some((n) => n.isShort);
    if (!hasShort) return prompts;

    // Gather short neighbors and their far-side (second) neighbors
    const shortNeighbors: LinePrompt[] = [];
    const secondNeighbors: LinePrompt[] = [];

    for (const { neighbor, isShort } of neighbors) {
      if (!isShort) continue;
      shortNeighbors.push(neighbor);
    }

    const shortNeighborSet = new Set<LinePrompt>(shortNeighbors);
    for (const shortNeighbor of shortNeighbors) {
      if (!shortNeighbor.connections) continue;
      for (const side of ['positive', 'negative'] as const) {
        const farConn: PromptConnection | undefined = shortNeighbor.connections[side];
        if (!farConn) continue;
        const farIdx = farConn.targetPromptIndex;
        if (farIdx < 0 || farIdx >= prompts.length) continue;
        const farPrompt = prompts[farIdx];
        if (farPrompt !== targetPrompt && !shortNeighborSet.has(farPrompt)) {
          secondNeighbors.push(farPrompt);
        }
      }
    }

    if (shortNeighbors.length === 0) return prompts;

    const cosThreshold = 0.85;
    const extent = PromptAligner.selectionExtent(prompts);
    const collinearThreshold = extent * 0.08;

    const removeTargetPrompt = shortNeighbors.length === 2;
    let mergePair: [LinePrompt, LinePrompt] | null = null;

    if (secondNeighbors.length === 2) {
      const [a, b] = secondNeighbors;
      if (PromptAligner.areCollinear(a, b, cosThreshold, collinearThreshold, this.coordinateTransformer)) {
        mergePair = [a, b];
      }
    }
    if (!mergePair) {
      for (const secondNeighbor of secondNeighbors) {
        if (PromptAligner.areCollinear(targetPrompt, secondNeighbor, cosThreshold, collinearThreshold, this.coordinateTransformer)) {
          mergePair = [targetPrompt, secondNeighbor];
          break;
        }
      }
    }

    const promptsToRemove = new Set<LinePrompt>(shortNeighbors);
    if (removeTargetPrompt) {
      promptsToRemove.add(targetPrompt);
    }

    for (let i = prompts.length - 1; i >= 0; i--) {
      if (promptsToRemove.has(prompts[i])) {
        prompts.splice(i, 1);
      }
    }
    onShapeUpdate(prompts);

    if (mergePair && prompts.includes(mergePair[0]) && prompts.includes(mergePair[1])) {
      this.mergePromptPair(mergePair[0], mergePair[1], prompts, floorHeight);
    }

    onShapeUpdate(prompts);
    return prompts;
  }
}
